package com.ijoin.ui.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.LegalDocument
import com.ijoin.data.model.LegalDocumentType
import com.ijoin.data.repository.LegalRepository
import com.ijoin.i18n.strings
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventDialog
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import kotlinx.coroutines.launch

/**
 * Dialog shown before first AI feature usage.
 * Explains what data is sent to OpenAI/Google and asks for consent.
 * Uses existing legal acceptance infrastructure to record consent in DB.
 */
@Composable
fun AiConsentDialog(
    onAccepted: () -> Unit,
    onDeclined: () -> Unit,
    legalRepository: LegalRepository = remember { LegalRepository() }
) {
    val strings = strings()
    val scope = rememberCoroutineScope()

    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Full document viewing
    var showFullDocument by remember { mutableStateOf(false) }
    var fullDocument by remember { mutableStateOf<LegalDocument?>(null) }
    var isLoadingDocument by remember { mutableStateOf(false) }
    var documentError by remember { mutableStateOf<String?>(null) }

    EventDialog(onDismissRequest = onDeclined) {
        Text(
            text = strings.aiConsentTitle,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            color = EventCardAccentDark
        )

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(max = 400.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Text(
                text = strings.aiConsentDescription,
                style = MaterialTheme.typography.bodyMedium
            )

            Text(
                text = strings.aiConsentDataSent,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold
            )

            Column(
                modifier = Modifier.padding(start = 8.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text(
                    text = "\u2022 ${strings.aiConsentDataSentAudio}",
                    style = MaterialTheme.typography.bodyMedium
                )
                Text(
                    text = "\u2022 ${strings.aiConsentDataSentDescription}",
                    style = MaterialTheme.typography.bodyMedium
                )
                Text(
                    text = "\u2022 ${strings.aiConsentDataSentLanding}",
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            Text(
                text = strings.aiConsentProviders,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Text(
                text = strings.aiConsentOptional,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        if (error != null) {
            Text(
                text = error.orEmpty(),
                color = EventCardAlert,
                style = MaterialTheme.typography.bodySmall
            )
        }

        // View full document link — separate row
        EventOutlineButton(
            text = strings.viewDocument,
            onClick = {
                showFullDocument = true
                isLoadingDocument = true
                documentError = null
                scope.launch {
                    legalRepository.getDocument(LegalDocumentType.AI_DATA_CONSENT)
                        .onSuccess { doc ->
                            fullDocument = doc
                            isLoadingDocument = false
                        }
                        .onFailure { e ->
                            documentError = e.message
                            isLoadingDocument = false
                        }
                }
            },
            modifier = Modifier.fillMaxWidth()
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End)
        ) {
            EventOutlineButton(
                text = strings.aiConsentDecline,
                onClick = onDeclined
            )

            EventPrimaryButton(
                text = strings.aiConsentAccept,
                onClick = {
                    isLoading = true
                    error = null
                    scope.launch {
                        legalRepository.acceptAiConsent()
                            .onSuccess {
                                isLoading = false
                                onAccepted()
                            }
                            .onFailure { e ->
                                isLoading = false
                                error = e.message
                            }
                    }
                },
                enabled = !isLoading,
                isLoading = isLoading
            )
        }
    }

    if (showFullDocument) {
        LegalDocumentDialog(
            document = fullDocument,
            isLoading = isLoadingDocument,
            error = documentError,
            onDismiss = {
                showFullDocument = false
                fullDocument = null
                documentError = null
            }
        )
    }
}
